import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import testimonialsData from '../content/testimonials.json';

const iconMap = {
  Heart: Heart,
  Star: Star,
  Quote: Quote,
};

const TestimonialsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const { sectionTitle, description, testimonials, stats } = testimonialsData;

  const statsWithIcons = stats.map(stat => ({
    ...stat,
    icon: React.createElement(iconMap[stat.icon] || Heart, { size: 32 })
  }));

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Heart className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">המלצות</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Main Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="relative max-w-4xl mx-auto mb-16"
        >
          <div className="bg-gradient-to-br from-nitzan-sage/10 to-nitzan-mint/10 rounded-3xl p-8 md:p-12 shadow-2xl border border-nitzan-sage/20 relative overflow-hidden">
            {/* Decorative Quote */}
            <Quote className="absolute top-8 right-8 text-nitzan-mint/20" size={80} />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="relative z-10"
              >
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">{testimonials[currentIndex].image}</div>
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
                    ))}
                  </div>
                </div>

                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 text-center italic">
                  "{testimonials[currentIndex].text}"
                </p>

                <div className="text-center">
                  <p className="text-xl font-bold text-nitzan-teal">{testimonials[currentIndex].name}</p>
                  <p className="text-nitzan-terracotta">{testimonials[currentIndex].service}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <motion.button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-nitzan-teal hover:bg-nitzan-mint hover:text-white transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight size={24} />
            </motion.button>
            <motion.button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-nitzan-teal hover:bg-nitzan-mint hover:text-white transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft size={24} />
            </motion.button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-8 h-2 bg-nitzan-teal'
                    : 'w-2 h-2 bg-nitzan-sage/30 hover:bg-nitzan-sage/50'
                }`}
              />
            ))}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {statsWithIcons.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-gradient-to-br from-white to-nitzan-light-bg rounded-2xl p-8 shadow-lg border border-nitzan-sage/20 text-center hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-full flex items-center justify-center text-white mx-auto mb-4">
                {stat.icon}
              </div>
              <div className="text-4xl font-bold text-nitzan-teal mb-2">{stat.number}</div>
              <div className="text-gray-700">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="bg-gradient-to-r from-nitzan-sage/20 via-nitzan-mint/20 to-nitzan-teal/20 rounded-3xl p-8 md:p-12 text-center"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-4">רוצה להיות הבאה?</h3>
          <p className="text-xl text-gray-700 mb-6">
            בואי להתחיל את המסע שלך ולהצטרף למאות האנשים שכבר חוו שינוי
          </p>
          <Link to="/contact">
            <motion.div
              className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              צרי איתי קשר
            </motion.div>
          </Link>
        </motion.div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.0 }}
          className="text-center text-gray-500 text-sm mt-8"
        >
          * ההמלצות הן דוגמאות. ניתן להחליף בהמלצות אמיתיות.
        </motion.p>
      </div>
    </section>
  );
};

export default TestimonialsSection;

